/**
 * Documents page (Bill 2026-10-17): signed packets from account opening. Without a customer it shows the latest
 * packets; /rdl/cif/:cifId shows one customer's. The old library view read SharePoint rows that never carried the
 * customer, so it was always empty.
 */
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, Search } from 'lucide-react';
import { Button } from '@/components/primitives';
import { SignedPackets } from '@/components/documents/SignedPackets';
import { useSignedPackets } from '@/components/documents/useSignedPackets';

export function SignedPacketsPage(): JSX.Element {
  const { cifId } = useParams<{ cifId?: string }>();
  const navigate = useNavigate();
  const cif = cifId?.trim() || null;
  const { packets, loading, error } = useSignedPackets(cif);
  const customer = cif ? packets?.find((p) => p.primaryCif === cif)?.customer ?? `CIF ${cif}` : null;

  return (
    <div className="rdl-shell">
      {cif && (
        <Button variant="ghost" size="sm" startIcon={<ChevronLeft size={14} />} onClick={() => navigate('/rdl')}>
          All recent packets
        </Button>
      )}
      <header className="rdl-shell__header">
        <div>
          <h1 className="rdl-shell__title">{customer ?? 'Signed packets'}</h1>
          <p className="rdl-shell__sub">
            {cif
              ? 'Every account opening this customer was on: what they signed, what they were given to read, and what is on file.'
              : 'The latest account openings across the bank, newest first. Find a customer to see all of theirs.'}
          </p>
        </div>
        <div className="rdl-shell__actions">
          <Button variant="primary" size="sm" startIcon={<Search size={14} />}
            onClick={() => window.dispatchEvent(new CustomEvent('rop:find-customer'))}>
            Find a customer
          </Button>
        </div>
      </header>
      <SignedPackets packets={packets} loading={loading} error={error} showCustomerLink={!cif}
        emptyText={cif ? 'No signed packets for this customer yet.' : 'No signed packets yet.'} />
    </div>
  );
}
